import * as React from 'react'
import { NavMain } from '@/components/nav-main'
import { type Icon } from '@tabler/icons-react'
import { NavUser } from '@/components/nav-user'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  EventManagementIcon,
  SponsorAdManagementIcon,
  HomeIcon,
  UsersIcon,
  CategoryIcon,
  CalenderOutlineIcon,
  DocumentTextIcon,
  ChatIcon,
} from '@/components/Icons'
import type { User } from '@/schemas/user.schema'
import { USER_ROLES } from '@/constants'
import { tokenService } from '@/lib/token.service'
import { useUserRole } from '@/contexts/UserRoleContext'
import { PATHROUTES } from '@/constants/path'
import { useUnreadMessages } from '@/hooks/useUnreadMessages'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { toggleSidebar } = useSidebar()
  const { currentUser } = useUserRole()
  const userData = tokenService.getUser()

  const user: User = currentUser && currentUser.id ? currentUser : userData
  const userRoles: User['userRoles'] = user?.userRoles ?? []
  const roleNames = userRoles.map(role => role.name)
  const { unreadRoomsCount } = useUnreadMessages(user?.id)
  const isMessageAdmin = currentUser?.isChatAdmin

  const superAdminNav = [
    { title: 'Dashboard', url: '/dashboard', icon: HomeIcon },
    {
      title: 'User Management',
      url: '/dashboard/users-management',
      icon: UsersIcon,
    },
    {
      title: 'Category Management',
      url: '/dashboard/category-management',
      icon: CategoryIcon,
    },
    {
      title: 'Event Management',
      url: '/dashboard/event-management',
      icon: EventManagementIcon,
    },
    {
      title: 'Sponsor Ad Management',
      url: '/dashboard/sponsor-ad-management',
      icon: SponsorAdManagementIcon,
    },
    ...(isMessageAdmin
      ? [
          {
            title: 'Sponsor Messages',
            url: PATHROUTES.DASHBOARD.MESSAGE_ADMIN,
            icon: ChatIcon,
            badge: unreadRoomsCount,
          },
        ]
      : []),
  ]

  const eventAdminNav = [
    {
      title: 'Event Dashboard',
      url: '/dashboard/event-dashboard',
      icon: HomeIcon,
    },
    {
      title: 'Event Management',
      url: '/dashboard/event-management',
      icon: EventManagementIcon,
    },
    {
      title: 'Attendee Management',
      url: '/dashboard/attendee-management',
      icon: UsersIcon,
    },
  ]
  const sponsorAdminNav = [
    {
      title: 'My Sponsorships',
      url: '/dashboard/sponsorship-management',
      icon: DocumentTextIcon,
    },
    {
      title: 'Demographics',
      url: '/dashboard/demographics-dashboard',
      icon: CalenderOutlineIcon,
    },
  ]

  let navMain: Array<{
    title: string
    url: string
    icon?:
      | Icon
      | React.ComponentType<{ className?: string; strokeWidth?: number }>
    badge?: number
  }> = []
  if (roleNames.includes(USER_ROLES.superAdmin)) {
    navMain = superAdminNav
  } else if (roleNames.includes(USER_ROLES.eventAdmin)) {
    navMain = eventAdminNav
  } else if (roleNames.includes(USER_ROLES.sponsor)) {
    navMain = sponsorAdminNav
  }

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <nav aria-label="Sidebar navigation" className="w-full">
              <div className="flex items-start justify-between w-full">
                <Link
                  to="/dashboard"
                  className="font-medium"
                  aria-label="Go to dashboard home"
                  tabIndex={0}
                >
                  <img
                    src="/auth/logo.svg"
                    alt="SlopeSync logo, dashboard home"
                    className="w-24 h-24 size-24"
                  />
                </Link>

                {/* Close button - Visible at lg breakpoint and below */}
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={toggleSidebar}
                  className="lg:hidden p-0 ml-2"
                  aria-label="Close navigation sidebar"
                  type="button"
                  tabIndex={0}
                  autoFocus
                >
                  <X
                    className="h-18 w-18 text-ss-neutral-600"
                    aria-hidden="true"
                  />
                </Button>
              </div>
            </nav>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <nav aria-label="Main navigation">
          <NavMain items={navMain} />
        </nav>
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={user} />
      </SidebarFooter>
    </Sidebar>
  )
}
